import { Link } from "react-router-dom";
import { ArrowLeft, ChevronDown, ChevronUp } from "lucide-react";
import { useState } from "react";
import Footer from "@/components/Footer";

const FAQItem = ({ question, answer, isOpen, onClick }) => {
  return (
    <div className="border-b border-gray-200">
      <button
        className="w-full py-6 flex justify-between items-center text-left"
        onClick={onClick}
      >
        <span className="text-lg font-medium pr-8">{question}</span>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-gray-500 flex-shrink-0" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0" />
        )}
      </button>
      {isOpen && (
        <div className="pb-6 text-gray-600 leading-relaxed">
          {answer}
        </div>
      )}
    </div>
  );
};

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(0);

  const faqs = [
    {
      category: "Orders & Products",
      items: [
        {
          question: "What sizes are available?",
          answer: (
            <>
              We offer three poster sizes:
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li><strong>Small (12×16 inches):</strong> $25 — Perfect for desks, shelves, and small spaces</li>
                <li><strong>Medium (16×20 inches):</strong> $45 — Great for living rooms and offices</li>
                <li><strong>Large (20×30 inches):</strong> $65 — A bold statement piece for any room</li>
              </ul>
            </>
          )
        },
        {
          question: "What paper and print quality do you use?",
          answer: "All posters are printed on premium enhanced matte paper (189 gsm / 80 lb) using archival inks. This creates a smooth, professional finish with vibrant colors that won't fade over time. The matte finish eliminates glare, making your poster easy to read from any angle."
        },
        {
          question: "Can I enter more than three goals?",
          answer: "Currently, our posters are designed for up to three goals. We found that limiting to three keeps the design clean and helps you focus on what truly matters. Quality over quantity! If you have more goals, consider creating multiple posters for different areas of your life."
        },
        {
          question: "Do the posters come framed?",
          answer: "Posters ship unframed and rolled in a protective tube. This keeps shipping costs down and allows you to choose a frame that matches your decor. Our sizes (12×16, 16×20, 20×30) are standard frame dimensions, so finding frames at most home goods stores is easy."
        }
      ]
    },
    {
      category: "Shipping & Delivery",
      items: [
        {
          question: "How long does shipping take?",
          answer: (
            <>
              <p className="mb-2">Delivery times include production (2-5 business days) plus shipping:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li><strong>United States:</strong> 5-12 business days total</li>
                <li><strong>Canada:</strong> 7-15 business days total</li>
                <li><strong>Europe:</strong> 7-15 business days total</li>
                <li><strong>Australia/NZ:</strong> 10-20 business days total</li>
                <li><strong>Rest of World:</strong> 10-25 business days total</li>
              </ul>
              <p className="mt-2">You'll receive tracking information via email once your order ships.</p>
            </>
          )
        },
        {
          question: "Do you ship internationally?",
          answer: "Yes! We ship to most countries worldwide. Shipping rates and delivery times vary by location and are calculated at checkout based on your address. Our print-on-demand partner has facilities in multiple regions, so your poster is printed at the location closest to you to minimize shipping time."
        },
        {
          question: "How is shipping calculated?",
          answer: "Shipping costs are calculated based on your location and the size of your poster. Enter your shipping address during checkout to see exact rates. We partner with reliable carriers to ensure your poster arrives safely in a protective tube."
        },
        {
          question: "Can I track my order?",
          answer: "Yes! Once your poster ships, you'll receive an email with tracking information. You can use this to monitor your package's journey to your door. If you haven't received tracking info within 7 business days of ordering, please contact our support team."
        }
      ]
    },
    {
      category: "Returns & Refunds",
      items: [
        {
          question: "What is your return policy?",
          answer: (
            <>
              <p className="mb-2">Since each poster is custom-made with your personal goals, we cannot accept returns for change of mind. However, we will replace or refund your order if:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>The poster arrives damaged or defective</li>
                <li>The print quality is significantly below standard</li>
                <li>Your order was lost in transit</li>
              </ul>
              <p className="mt-2">Please contact us within 14 days of delivery with photos of any issues.</p>
            </>
          )
        },
        {
          question: "My poster arrived damaged. What do I do?",
          answer: "We're sorry to hear that! Please email support@cue.po within 14 days of receiving your order. Include your order number and clear photos showing the damage. We'll arrange a replacement at no additional cost."
        },
        {
          question: "Can I cancel my order?",
          answer: "Orders can be cancelled within 2 hours of placing them, before they enter production. After that, your poster is being printed and cannot be cancelled. Contact us immediately at support@cue.po if you need to cancel."
        }
      ]
    },
    {
      category: "Payment & Promo Codes",
      items: [
        {
          question: "What payment methods do you accept?",
          answer: "We accept all major credit cards (Visa, Mastercard, American Express, Discover), debit cards, and other payment methods supported by Stripe including Apple Pay and Google Pay. All payments are processed securely through Stripe."
        },
        {
          question: "Do you have any promo codes?",
          answer: (
            <>
              <p className="mb-2">We occasionally offer promotional discounts. Here are some codes that may be active:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li><strong>WELCOME10:</strong> 10% off your first order</li>
                <li><strong>SAVE5:</strong> $5 off any order</li>
                <li><strong>FREESHIP:</strong> Free shipping on your order</li>
              </ul>
              <p className="mt-2">Follow us on Instagram @cue.po for exclusive promo codes and announcements!</p>
            </>
          )
        },
        {
          question: "Is my payment information secure?",
          answer: "Absolutely. All payments are processed through Stripe, a leading payment processor that is PCI-DSS compliant. We never store your credit card information on our servers. Your payment details are encrypted and transmitted securely."
        }
      ]
    }
  ];

  // Flatten all FAQ items for indexing
  const allItems = faqs.flatMap(category => category.items);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <header className="px-6 md:px-12 lg:px-24 py-8 border-b border-gray-200">
        <div className="max-w-[1800px] mx-auto flex justify-between items-center">
          <Link to="/" className="font-['Syne'] font-extrabold text-2xl tracking-tight">
            CUE.PO
          </Link>
          <Link 
            to="/"
            className="flex items-center gap-2 text-gray-500 hover:text-black transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 px-6 md:px-12 lg:px-24 py-16">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-extrabold mb-4">Frequently Asked Questions</h1>
          <p className="text-gray-500 text-lg mb-12">
            Find answers to common questions about CUE.PO posters.
          </p>

          {faqs.map((category, catIndex) => (
            <div key={catIndex} className="mb-12">
              <h2 className="text-2xl font-bold mb-6 text-[#002FA7]">{category.category}</h2>
              <div>
                {category.items.map((item, itemIndex) => {
                  const globalIndex = faqs
                    .slice(0, catIndex)
                    .reduce((acc, cat) => acc + cat.items.length, 0) + itemIndex;
                  
                  return (
                    <FAQItem
                      key={itemIndex}
                      question={item.question}
                      answer={item.answer}
                      isOpen={openIndex === globalIndex}
                      onClick={() => setOpenIndex(openIndex === globalIndex ? -1 : globalIndex)}
                    />
                  );
                })}
              </div>
            </div>
          ))}

          {/* Still have questions */}
          <div className="bg-gray-50 p-8 rounded-sm text-center mt-12">
            <h2 className="text-2xl font-bold mb-4">Still have questions?</h2>
            <p className="text-gray-600 mb-6">
              Can't find what you're looking for? Our team is happy to help.
            </p>
            <Link to="/contact">
              <button className="btn-primary">
                Contact Us
              </button>
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default FAQ;
